// src/components/booking/useMonthAvailability.ts
// Hook de disponibilidad mensual. Extraído de BookingModal.tsx SIN cambios de lógica.
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { startOfMonth, endOfMonth, addDays, toYMD, slotIsFuture } from './bookingShared';
import { loadAvailability } from './loadAvailability';

/* ========================= disponibilidad mensual ========================= */
export function useMonthAvailability({
  clientId, dealershipId, monthDate, tz,
}: { clientId?: number; dealershipId?: number | null; monthDate: Date; tz: string; }) {
  const cacheRef = useRef<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [availableDays, setAvailableDays] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (!clientId || !dealershipId) { setAvailableDays({}); return; }
    setLoading(true);
    const start = startOfMonth(monthDate);
    const end = endOfMonth(monthDate);

    const days: string[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) days.push(toYMD(d));

    const result: Record<string, boolean> = {};
    const pending: string[] = [];

    for (const ymd of days) {
      const key = `${clientId}:${dealershipId}:${ymd}`;
      if (cacheRef.current[key] !== undefined) result[ymd] = cacheRef.current[key];
      else pending.push(ymd);
    }

    const CONCURRENCY = 5;
    let idx = 0;
    async function worker() {
      while (idx < pending.length) {
        const ymd = pending[idx++];
        // `tz` es la zona del NEGOCIO (ver businessTimezone). Sin pasarla, el
        // calendario deshabilitaba días enteros según el reloj del visitante.
        // Con las RPC `_utc` la pregunta "¿queda alguna hora?" se contesta
        // comparando instantes; sin ellas, con el reloj de pared de siempre.
        let has = false;
        try {
          const { slots } = await loadAvailability(clientId!, dealershipId!, ymd, tz);
          has = slots.some((s) => slotIsFuture(s, ymd, tz));
        } catch {
          has = false;
        }
        result[ymd] = has;
        cacheRef.current[`${clientId}:${dealershipId}:${ymd}`] = has;
      }
    }
    const workers = Array.from({ length: Math.min(CONCURRENCY, pending.length) }, () => worker());
    await Promise.all(workers);

    setAvailableDays(result);
    setLoading(false);
  }, [clientId, dealershipId, monthDate, tz]);

  useEffect(() => { load(); }, [load]);

  return { loading, availableDays };
}

export default useMonthAvailability;
